import { FileExcelOutlined, FileImageOutlined, FilePdfOutlined, FilePptOutlined, FileTextOutlined, FileWordOutlined, VideoCameraOutlined } from "@ant-design/icons";
import { Spin, Tooltip } from "antd";
import { getUrl } from "aws-amplify/storage";
import { useCallback, useEffect, useRef, useState } from "react";

const getFileTypeIcon = (fileType) => {
    if (fileType.startsWith("image/")) {
        return <FileImageOutlined className="text-blue-500" />;
    } else if (fileType.startsWith("video/")) {
        return <VideoCameraOutlined className="text-purple-500" />;
    } else if (fileType === "application/pdf") {
        return <FilePdfOutlined className="text-red-500" />;
    } else if (fileType.includes("word") || fileType.includes("document")) {
        return <FileWordOutlined className="text-blue-600" />;
    } else if (fileType.includes("sheet") || fileType.includes("excel")) {
        return <FileExcelOutlined className="text-green-600" />;
    } else if (fileType.includes("presentation") || fileType.includes("powerpoint")) {
        return <FilePptOutlined className="text-orange-500" />;
    }
    return <FileTextOutlined className="text-gray-500" />;
};

const AttachmentThumbnail = ({ file, size = "medium", showPreview = true, onPreview, className = "", lazy = true }) => {
    const [thumbnail, setThumbnail] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(false);
    const [isVisible, setIsVisible] = useState(!lazy);
    const imgRef = useRef(null);
    const canvasRef = useRef(null);

    const sizeClasses = {
        small: "w-12 h-12",
        medium: "w-24 h-24",
        large: "w-32 h-32",
    };

    const generateImageThumbnail = useCallback(
        async (imageUrl) => {
            return new Promise((resolve, reject) => {
                const img = new Image();
                img.crossOrigin = "anonymous";

                img.onload = () => {
                    const canvas = canvasRef.current;
                    if (!canvas) {
                        reject(new Error("Canvas not available"));
                        return;
                    }

                    const ctx = canvas.getContext("2d");
                    const maxSize = size === "small" ? 48 : size === "medium" ? 80 : 128;

                    let { width, height } = img;
                    if (width > height) {
                        height = (height * maxSize) / width;
                        width = maxSize;
                    } else {
                        width = (width * maxSize) / height;
                        height = maxSize;
                    }

                    canvas.width = width;
                    canvas.height = height;

                    ctx.drawImage(img, 0, 0, width, height);
                    const thumbnailDataUrl = canvas.toDataURL("image/jpeg", 0.8);
                    resolve(thumbnailDataUrl);
                };

                img.onerror = () => reject(new Error("Failed to load image"));
                img.src = imageUrl;
            });
        },
        [size],
    );

    const loadThumbnail = useCallback(async () => {
        if (!file?.key) return;

        setLoading(true);
        setError(false);

        try {
            if (file?.type?.startsWith("image/")) {
                const urlResult = await getUrl({ path: `public/${file.key}` });
                const imageUrl = urlResult.url.toString();
                try {
                    const thumbnailDataUrl = await generateImageThumbnail(imageUrl);
                    setThumbnail(thumbnailDataUrl);
                } catch (thumbErr) {
                    // Canvas downscaling can fail (e.g. a CORS-tainted S3 URL throws on
                    // toDataURL). Fall back to rendering the full image directly so the
                    // user still gets a preview instead of an error tile.
                    console.warn("Thumbnail generation failed, falling back to full image:", thumbErr);
                    setThumbnail(imageUrl);
                }
            } else {
                setThumbnail(null);
            }
        } catch (err) {
            console.error("Error generating thumbnail:", err);
            setError(true);
        } finally {
            setLoading(false);
        }
    }, [file?.key, file?.type, generateImageThumbnail]);

    useEffect(() => {
        if (!lazy || !imgRef.current) return;

        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) {
                    setIsVisible(true);
                    observer.disconnect();
                }
            },
            { threshold: 0.1 },
        );

        observer.observe(imgRef.current);
        return () => observer.disconnect();
    }, [lazy]);

    useEffect(() => {
        if (isVisible && file?.key) {
            loadThumbnail();
        }
    }, [isVisible, file?.key, loadThumbnail]);

    const handleClick = () => {
        if (onPreview && file) {
            onPreview(file);
        }
    };

    return (
        <div ref={imgRef} className={`relative ${className}`}>
            <canvas ref={canvasRef} className="hidden" />

            <div className={`${sizeClasses[size]} mx-auto flex items-center justify-center overflow-hidden rounded-lg border border-gray-200 bg-gray-50 p-2 shadow-sm`}>
                {loading ? (
                    <Spin size="small" />
                ) : error ? (
                    <div className="text-center text-xs text-red-500">
                        <div className="mb-1 text-lg">⚠️</div>
                        <div>Error</div>
                    </div>
                ) : thumbnail ? (
                    <img src={thumbnail} alt={file?.name || "Thumbnail"} className="h-full w-full object-cover" />
                ) : (
                    <div className="text-center">
                        <div className="mb-1 text-2xl">{getFileTypeIcon(file?.type || "")}</div>
                        <div className="truncate px-1 text-xs text-gray-500">{file?.name?.split(".").pop()?.toUpperCase() || "FILE"}</div>
                    </div>
                )}
            </div>

            {file?.name && (
                <Tooltip title={file.name} placement="top">
                    <div className="mt-4 text-center">
                        <div className="mx-auto max-w-[120px] truncate text-xs text-gray-600">{file.name}</div>
                    </div>
                </Tooltip>
            )}
        </div>
    );
};

export default AttachmentThumbnail;
